import {
  Box,
  Button,
  HStack,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router";

import FormSwitch from "@/common/components/Form/FormSwitch";
import { IcoAlert, IcoLinear, IconValueKey } from "@/common/components/Icons";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import { resolveLinearIconAndColor } from "@reflag/shared/icons";
import { FeatureSettingsUrl } from "@reflag/shared/urls";

type Props = {
  feature: FeatureDetail;
  missingMatch?: string;
  onChange?: (
    result: {
      icon: IconValueKey | null;
      iconColor: string | null;
    } | null,
  ) => void;
};

export function LinearIconSyncToggle({
  feature,
  missingMatch,
  onChange,
}: Props) {
  const { env, org } = useEnvironment();
  const navigate = useNavigate();
  const missingMatchWarningColor = useColorModeValue(
    "orange.500",
    "yellow.600",
  );

  const hasLinearIntegration = !!org.integrations.linear;
  if (!hasLinearIntegration) {
    return null;
  }

  const trackingEntity =
    hasLinearIntegration && feature.integrations.linear?.trackingEntity;

  if (!trackingEntity) {
    return (
      <Tooltip
        label="This flag is not linked to a Linear issue or project. To enable icon sync, connect a to one in the flag's Linear settings."
        openDelay={300}
        placement="top"
        hasArrow
      >
        <Button
          color="dimmed"
          leftIcon={<IcoLinear size="sm" />}
          variant="ghost"
          onClick={() => {
            navigate(FeatureSettingsUrl(env, feature, "linear"));
          }}
        >
          No issue/project
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      label={
        missingMatch
          ? `Could not find a matching icon for Linear icon "${missingMatch}", please select another icon.`
          : undefined
      }
      openDelay={300}
      placement="top"
      hasArrow
    >
      <Box position="relative">
        <FormSwitch
          _control={{
            alignItems: "center",
          }}
          label={
            <HStack align="center" cursor="pointer" gap={2}>
              <IcoLinear size="sm" />
              <Text>Sync</Text>
            </HStack>
          }
          mr={2}
          name="autoSyncLinearIcon"
          size="sm"
          onChange={(e) => {
            onChange?.(
              e.target.checked
                ? resolveLinearIconAndColor(trackingEntity)
                : null,
            );
          }}
        />
        {missingMatch && (
          <IcoAlert
            color={missingMatchWarningColor}
            position="absolute"
            right={-1}
            size="sm"
            top={-2}
          />
        )}
      </Box>
    </Tooltip>
  );
}
